import React, { useState, useEffect } from "react";
import { 
  Shield, 
  FileText, 
  CheckCircle, 
  Download, 
  Send, 
  RefreshCw, 
  Cpu, 
  Radio, 
  Layers, 
  ExternalLink,
  ChevronRight,
  Clock,
  Award,
  AlertTriangle
} from "lucide-react";

interface HardwarePreset {
  id: string;
  type_label: string;
  name: string;
  cipher: string;
  tu_number: string;
  nato_class: string;
  nato_nsn: string;
  specs: [string, string][];
}

interface HistoryItem {
  id: string;
  folder: string;
  createdAt: string;
  files: string[];
}

export const MilitaryCodifierAgentView: React.FC = () => {
  const [catalog, setCatalog] = useState<HardwarePreset[]>([]);
  const [selectedType, setSelectedType] = useState<string>("acoustic_infrasound");
  const [productName, setProductName] = useState<string>("");
  const [cipher, setCipher] = useState<string>("");
  const [developer, setDeveloper] = useState<string>("Банах Андрій Степанович");
  const [rnokpp, setRnokpp] = useState<string>("2713213814");
  const [orcid, setOrcid] = useState<string>("0009-0000-2933-1143");
  const [phone, setPhone] = useState<string>("+38 (063) 325 38 48");
  const [email, setEmail] = useState<string>("bass776@ukr.net");
  const [tuNumber, setTuNumber] = useState<string>("");
  const [specs, setSpecs] = useState<[string, string][]>([]);
  
  const [loading, setLoading] = useState<boolean>(false);
  const [sendTelegram, setSendTelegram] = useState<boolean>(true);
  const [generationResult, setGenerationResult] = useState<any>(null);
  const [history, setHistory] = useState<HistoryItem[]>([]);
  const [activeTab, setActiveTab] = useState<"generator" | "history" | "guidance">("generator");

  // Завантаження каталогу
  useEffect(() => {
    fetch("/api/defense/codifier/catalog")
      .then(res => res.json())
      .then(data => {
        if (data.success && data.catalog) {
          setCatalog(data.catalog);
          const first = data.catalog[0];
          if (first) {
            applyPreset(first);
          }
        }
      })
      .catch(err => console.error("Error fetching codifier catalog:", err));

    loadHistory();
  }, []);

  const loadHistory = () => {
    fetch("/api/defense/codifier/history")
      .then(res => res.json())
      .then(data => {
        if (data.success && data.history) {
          setHistory(data.history);
        }
      })
      .catch(err => console.error("Error fetching history:", err));
  };

  const applyPreset = (preset: HardwarePreset) => {
    setSelectedType(preset.id);
    setProductName(preset.name);
    setCipher(preset.cipher);
    setTuNumber(preset.tu_number);
    setSpecs(preset.specs);
  };

  const handleTypeChange = (typeId: string) => {
    const found = catalog.find(c => c.id === typeId);
    if (found) {
      applyPreset(found);
    }
  };

  const handleSpecChange = (index: number, val: string) => {
    const newSpecs = [...specs];
    newSpecs[index][1] = val;
    setSpecs(newSpecs);
  };

  const handleGenerate = async () => {
    setLoading(true);
    setGenerationResult(null);
    try {
      const payload = {
        hardwareType: selectedType,
        sendTelegram: sendTelegram,
        customOverrides: {
          name: productName,
          cipher: cipher,
          developer: developer,
          rnokpp: rnokpp,
          orcid: orcid,
          phone: phone,
          email: email,
          tu_number: tuNumber,
          specs: specs
        }
      };

      const res = await fetch("/api/defense/codifier/generate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      setGenerationResult(data);
      loadHistory();
    } catch (err: any) {
      alert("Помилка при генерації: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 p-4 md:p-8 font-sans">
      {/* Верхня панель */}
      <div className="max-w-7xl mx-auto mb-8">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-slate-800 pb-6">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <span className="p-2.5 bg-amber-500/10 border border-amber-500/30 rounded-xl text-amber-400">
                <Shield className="w-8 h-8" />
              </span>
              <div>
                <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-white flex items-center gap-3">
                  Fast-Track Кодифікатор ОВТ МОУ & Brave1
                  <span className="text-xs px-2.5 py-1 bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 rounded-full font-mono uppercase">
                    Постанова КМУ № 345
                  </span>
                </h1>
                <p className="text-slate-400 text-sm">
                  Автоматизована підготовка повного пакету конструкторської та відомчої документації для допуску до експлуатації та кодифікації за стандартами НАТО (STANAG 3151 / NSN)
                </p>
              </div>
            </div>
          </div>

          {/* Перемикач вкладок */}
          <div className="flex items-center bg-slate-900 border border-slate-800 rounded-xl p-1">
            <button
              onClick={() => setActiveTab("generator")}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors flex items-center gap-2 ${
                activeTab === "generator" 
                  ? "bg-amber-600 text-white shadow-lg shadow-amber-600/30" 
                  : "text-slate-400 hover:text-white"
              }`}
            >
              <Cpu className="w-4 h-4" /> Генератор Пакету
            </button>
            <button
              onClick={() => setActiveTab("history")}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors flex items-center gap-2 ${
                activeTab === "history" 
                  ? "bg-amber-600 text-white shadow-lg shadow-amber-600/30" 
                  : "text-slate-400 hover:text-white"
              }`}
            >
              <Clock className="w-4 h-4" /> Історія Релізів ({history.length})
            </button>
            <button
              onClick={() => setActiveTab("guidance")}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors flex items-center gap-2 ${
                activeTab === "guidance" 
                  ? "bg-amber-600 text-white shadow-lg shadow-amber-600/30" 
                  : "text-slate-400 hover:text-white"
              }`}
            >
              <Award className="w-4 h-4" /> Регламент МОУ
            </button>
          </div>
        </div>

        {/* Нормативна стрічка бейджів */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4 text-xs font-mono">
          <div className="bg-slate-900/60 border border-slate-800 p-2.5 rounded-lg flex items-center gap-2">
            <CheckCircle className="w-4 h-4 text-emerald-400 shrink-0" />
            <span className="text-slate-300">Постанова КМУ № 345 (Спрощений допуск)</span>
          </div>
          <div className="bg-slate-900/60 border border-slate-800 p-2.5 rounded-lg flex items-center gap-2">
            <CheckCircle className="w-4 h-4 text-emerald-400 shrink-0" />
            <span className="text-slate-300">Постанова КМУ № 256 (БПЛА & БК)</span>
          </div>
          <div className="bg-slate-900/60 border border-slate-800 p-2.5 rounded-lg flex items-center gap-2">
            <CheckCircle className="w-4 h-4 text-cyan-400 shrink-0" />
            <span className="text-slate-300">STANAG 3151 / ACodP-1 (Код NSN)</span>
          </div>
          <div className="bg-slate-900/60 border border-slate-800 p-2.5 rounded-lg flex items-center gap-2">
            <CheckCircle className="w-4 h-4 text-amber-400 shrink-0" />
            <span className="text-slate-300">ДСТУ ГОСТ 2.601 / ДСТУ 1.5</span>
          </div>
        </div>
      </div>

      {/* Основний контент */}
      <div className="max-w-7xl mx-auto">
        {activeTab === "generator" && (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            {/* Ліва колонка: Форма введення та вибору */}
            <div className="lg:col-span-7 space-y-6">
              {/* Вибір категорії виробу */}
              <div className="bg-slate-900/80 border border-slate-800 rounded-2xl p-6 shadow-xl">
                <h3 className="text-base font-semibold text-white mb-4 flex items-center gap-2">
                  <Layers className="w-5 h-5 text-amber-400" />
                  1. Категорія оборонного виробу (Зразок ОВТ)
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {catalog.map(cat => (
                    <button
                      key={cat.id}
                      onClick={() => handleTypeChange(cat.id)}
                      className={`text-left p-3.5 rounded-xl border transition-all ${
                        selectedType === cat.id
                          ? "bg-amber-500/10 border-amber-500/60 shadow-lg shadow-amber-500/10 text-white"
                          : "bg-slate-950/60 border-slate-800 hover:border-slate-700 text-slate-300"
                      }`}
                    >
                      <div className="font-semibold text-sm mb-1">{cat.type_label}</div>
                      <div className="text-xs text-slate-400 font-mono">Шифр: {cat.cipher}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Реквізити виробу та розробника */}
              <div className="bg-slate-900/80 border border-slate-800 rounded-2xl p-6 shadow-xl space-y-4">
                <h3 className="text-base font-semibold text-white mb-2 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-amber-400" />
                  2. Ідентифікаційні дані зразка та розробника
                </h3>

                <div>
                  <label className="block text-xs font-medium text-slate-400 uppercase tracking-wider mb-1.5">
                    Повна назва виробу (предмета постачання)
                  </label>
                  <input
                    type="text"
                    value={productName}
                    onChange={e => setProductName(e.target.value)}
                    className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-2.5 text-sm text-white focus:outline-none focus:border-amber-500"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-slate-400 uppercase tracking-wider mb-1.5">
                      Децимальний номер / Шифр
                    </label>
                    <input
                      type="text"
                      value={cipher}
                      onChange={e => setCipher(e.target.value)}
                      className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-2.5 text-sm text-white focus:outline-none focus:border-amber-500 font-mono"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-400 uppercase tracking-wider mb-1.5">
                      Номер Технічних умов (ТУ У)
                    </label>
                    <input
                      type="text"
                      value={tuNumber}
                      onChange={e => setTuNumber(e.target.value)}
                      className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-2.5 text-sm text-white focus:outline-none focus:border-amber-500 font-mono"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-slate-400 uppercase tracking-wider mb-1.5">
                      Автор / Розробник (Виробник)
                    </label>
                    <input
                      type="text"
                      value={developer}
                      onChange={e => setDeveloper(e.target.value)}
                      className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-2.5 text-sm text-white focus:outline-none focus:border-amber-500 font-semibold"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-400 uppercase tracking-wider mb-1.5">
                      РНОКПП / Код ЄДРПОУ
                    </label>
                    <input
                      type="text"
                      value={rnokpp}
                      onChange={e => setRnokpp(e.target.value)}
                      className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-2.5 text-sm text-white focus:outline-none focus:border-amber-500 font-mono"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-slate-400 uppercase tracking-wider mb-1.5">
                      ORCID iD
                    </label>
                    <input
                      type="text"
                      value={orcid}
                      onChange={e => setOrcid(e.target.value)}
                      className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-2.5 text-sm text-white focus:outline-none focus:border-amber-500 font-mono"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-400 uppercase tracking-wider mb-1.5">
                      Контактний телефон
                    </label>
                    <input
                      type="text"
                      value={phone}
                      onChange={e => setPhone(e.target.value)}
                      className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-2.5 text-sm text-white focus:outline-none focus:border-amber-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-400 uppercase tracking-wider mb-1.5">
                      Електронна пошта
                    </label>
                    <input
                      type="text"
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                      className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-2.5 text-sm text-white focus:outline-none focus:border-amber-500"
                    />
                  </div>
                </div>
              </div>

              {/* Таблиця ТТХ */}
              <div className="bg-slate-900/80 border border-slate-800 rounded-2xl p-6 shadow-xl">
                <h3 className="text-base font-semibold text-white mb-3 flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <Radio className="w-5 h-5 text-amber-400" />
                    3. Тактико-технічні характеристики (ТТХ)
                  </span>
                  <span className="text-xs text-slate-400 font-normal">
                    {specs.length} параметрів
                  </span>
                </h3>
                <div className="space-y-3 max-h-80 overflow-y-auto pr-1">
                  {specs.map(([paramName, paramVal], idx) => (
                    <div key={idx} className="bg-slate-950/70 border border-slate-800/80 p-3 rounded-xl">
                      <div className="text-xs text-slate-300 font-medium mb-1.5">{paramName}</div>
                      <input
                        type="text"
                        value={paramVal}
                        onChange={e => handleSpecChange(idx, e.target.value)}
                        className="w-full bg-slate-900 border border-slate-800 rounded-lg px-3 py-1.5 text-xs text-amber-300 focus:outline-none focus:border-amber-500 font-mono"
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* Кнопка запуску */}
              <div className="bg-gradient-to-r from-amber-950/40 via-slate-900 to-slate-900 border border-amber-500/30 rounded-2xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      id="tgCheck"
                      checked={sendTelegram}
                      onChange={e => setSendTelegram(e.target.checked)}
                      className="w-4 h-4 accent-amber-500 rounded"
                    />
                    <label htmlFor="tgCheck" className="text-sm text-slate-200 cursor-pointer">
                      Миттєво надіслати весь пакет (.docx + .md) у Telegram розробнику (<span className="text-amber-400 font-mono">@Antigravity_cli_contabo_bot</span>)
                    </label>
                  </div>
                </div>

                <button
                  onClick={handleGenerate}
                  disabled={loading}
                  className="w-full py-4 bg-gradient-to-r from-amber-600 via-amber-500 to-yellow-600 hover:from-amber-500 hover:to-yellow-500 text-slate-950 font-bold rounded-xl shadow-lg shadow-amber-500/25 transition-all flex items-center justify-center gap-3 text-base disabled:opacity-50"
                >
                  {loading ? (
                    <>
                      <RefreshCw className="w-5 h-5 animate-spin" />
                      Формування 7 офіційних документів за ДСТУ...
                    </>
                  ) : (
                    <>
                      <Shield className="w-5 h-5" />
                      Згенерувати повний пакет кодифікації МОУ (7 документів)
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Права колонка: Результати генерації та список документів */}
            <div className="lg:col-span-5 space-y-6">
              {/* Картка складу пакету */}
              <div className="bg-slate-900/80 border border-slate-800 rounded-2xl p-6 shadow-xl">
                <h3 className="text-base font-semibold text-white mb-4 flex items-center gap-2">
                  <Award className="w-5 h-5 text-emerald-400" />
                  Склад офіційного комплекту за Постановою № 345
                </h3>
                <div className="space-y-3 text-xs">
                  <div className="p-3 bg-slate-950/70 border border-slate-800 rounded-xl">
                    <div className="font-semibold text-slate-200 mb-1 flex items-center gap-2">
                      <span className="w-5 h-5 rounded-full bg-amber-500/20 text-amber-400 flex items-center justify-center font-bold">1</span>
                      01. Офіційний Лист-опис зразка ОВТ
                    </div>
                    <p className="text-slate-400">Супровідна заявка до ГУЗР ОВТ МОУ на спрощений допуск.</p>
                  </div>

                  <div className="p-3 bg-slate-950/70 border border-slate-800 rounded-xl">
                    <div className="font-semibold text-slate-200 mb-1 flex items-center gap-2">
                      <span className="w-5 h-5 rounded-full bg-amber-500/20 text-amber-400 flex items-center justify-center font-bold">2</span>
                      02. Картка ТТХ та Таблиця відповідності ТТВ
                    </div>
                    <p className="text-slate-400">Гарантовані ТТХ (12 параметрів) та 100% захист від РЕБ.</p>
                  </div>

                  <div className="p-3 bg-slate-950/70 border border-slate-800 rounded-xl">
                    <div className="font-semibold text-slate-200 mb-1 flex items-center gap-2">
                      <span className="w-5 h-5 rounded-full bg-amber-500/20 text-amber-400 flex items-center justify-center font-bold">3</span>
                      03. Програма і методика випробувань (ПМВ)
                    </div>
                    <p className="text-slate-400">За ДСТУ ГОСТ 2.610 (кліматика, вібрації, РЕБ, IP67).</p>
                  </div>

                  <div className="p-3 bg-slate-950/70 border border-slate-800 rounded-xl">
                    <div className="font-semibold text-slate-200 mb-1 flex items-center gap-2">
                      <span className="w-5 h-5 rounded-full bg-amber-500/20 text-amber-400 flex items-center justify-center font-bold">4</span>
                      04. Акт та протокол відомчих випробувань
                    </div>
                    <p className="text-slate-400">Формалізований акт спільної відомчої комісії з висновком.</p>
                  </div>

                  <div className="p-3 bg-slate-950/70 border border-slate-800 rounded-xl">
                    <div className="font-semibold text-slate-200 mb-1 flex items-center gap-2">
                      <span className="w-5 h-5 rounded-full bg-amber-500/20 text-amber-400 flex items-center justify-center font-bold">5</span>
                      05. Проєкт Технічних умов (ТУ У)
                    </div>
                    <p className="text-slate-400">За ДСТУ 1.5:2015 для реєстрації та серійного виробництва.</p>
                  </div>

                  <div className="p-3 bg-slate-950/70 border border-slate-800 rounded-xl">
                    <div className="font-semibold text-slate-200 mb-1 flex items-center gap-2">
                      <span className="w-5 h-5 rounded-full bg-amber-500/20 text-amber-400 flex items-center justify-center font-bold">6</span>
                      06. Експлуатаційна документація (РЕ та ПС)
                    </div>
                    <p className="text-slate-400">Настанова щодо експлуатування та Паспорт за ДСТУ ГОСТ 2.601.</p>
                  </div>

                  <div className="p-3 bg-slate-950/70 border border-slate-800 rounded-xl">
                    <div className="font-semibold text-slate-200 mb-1 flex items-center gap-2">
                      <span className="w-5 h-5 rounded-full bg-amber-500/20 text-amber-400 flex items-center justify-center font-bold">7</span>
                      07. Заявка на код НАТО (NSN STANAG 3151)
                    </div>
                    <p className="text-slate-400">Форма ACodP-1 до Національного бюро кодифікації NCB Ukraine.</p>
                  </div>
                </div>
              </div>

              {/* Вікно щойно згенерованого результату */}
              {generationResult && generationResult.success && (
                <div className="bg-emerald-950/30 border border-emerald-500/40 rounded-2xl p-6 shadow-2xl">
                  <div className="flex items-center gap-2 text-emerald-400 font-semibold mb-3">
                    <CheckCircle className="w-5 h-5" />
                    Пакет успішно сформовано!
                  </div>
                  <p className="text-xs text-slate-300 mb-4 font-mono">
                    Директорія: {generationResult.out_dir}
                  </p>
                  <div className="space-y-2">
                    {generationResult.documents?.map((docPath: string, dIdx: number) => {
                      const fname = docPath.split("/").pop() || "";
                      const folder = generationResult.out_dir.split("/").pop() || "";
                      return (
                        <a
                          key={dIdx}
                          href={`/api/defense/codifier/download/${folder}/${fname}`}
                          download
                          className="flex items-center justify-between p-2.5 bg-slate-900 border border-slate-800 hover:border-emerald-500/50 rounded-xl text-xs text-slate-200 transition-colors"
                        >
                          <span className="truncate pr-2 font-mono">{fname}</span>
                          <span className="shrink-0 p-1 bg-emerald-500/20 text-emerald-400 rounded-lg">
                            <Download className="w-3.5 h-3.5" />
                          </span>
                        </a>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Вкладка: Історія релізів */}
        {activeTab === "history" && (
          <div className="bg-slate-900/80 border border-slate-800 rounded-2xl p-6 shadow-xl">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                <Clock className="w-5 h-5 text-amber-400" />
                Архів сформованих кодифікаційних пакетів ОВТ
              </h3>
              <button
                onClick={loadHistory}
                className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-xs rounded-lg text-slate-300 flex items-center gap-1.5"
              >
                <RefreshCw className="w-3.5 h-3.5" /> Оновити
              </button>
            </div>

            {history.length === 0 ? (
              <div className="text-center py-12 text-slate-500 text-sm">
                Ще немає сформованих пакетів. Створіть перший у вкладці «Генератор Пакету».
              </div>
            ) : (
              <div className="space-y-4">
                {history.map(item => (
                  <div key={item.id} className="bg-slate-950 border border-slate-800 rounded-xl p-4">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-3 pb-3 border-b border-slate-800/80">
                      <div>
                        <div className="text-sm font-semibold text-amber-400 font-mono">{item.id}</div>
                        <div className="text-xs text-slate-400">
                          Дата створення: {new Date(item.createdAt).toLocaleString("uk-UA")} • Документів: {item.files.length}
                        </div>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                      {item.files.map((fname, fIdx) => (
                        <a
                          key={fIdx}
                          href={`/api/defense/codifier/download/${item.id}/${fname}`}
                          download
                          className="flex items-center justify-between p-2 bg-slate-900 border border-slate-800 hover:border-amber-500/40 rounded-lg text-xs text-slate-300 transition-colors"
                        >
                          <span className="truncate pr-1 font-mono text-[11px]">{fname}</span>
                          <Download className="w-3.5 h-3.5 text-amber-400 shrink-0" />
                        </a>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Вкладка: Регламент МОУ */}
        {activeTab === "guidance" && (
          <div className="bg-slate-900/80 border border-slate-800 rounded-2xl p-6 md:p-8 shadow-xl space-y-6 text-sm text-slate-300 leading-relaxed">
            <h3 className="text-xl font-bold text-white flex items-center gap-2 pb-4 border-b border-slate-800">
              <Award className="w-6 h-6 text-amber-400" />
              Алгоритм проходження кодифікації ОВТ у воєнний час (Постанова КМУ № 345 / Brave1)
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-slate-950 border border-slate-800 p-4 rounded-xl">
                <div className="text-amber-400 font-bold mb-2 flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full bg-amber-500/20 text-amber-400 flex items-center justify-center text-xs">1</span>
                  Підготовка документів
                </div>
                <p className="text-xs text-slate-400">
                  Виробник формує повний пакет із 7 документів (Лист-опис, ТТХ, ПМВ, Акт випробувань, ТУ У, РЕ/ПС та форму НАТО). Наш сервіс генерує його за 5 секунд.
                </p>
              </div>

              <div className="bg-slate-950 border border-slate-800 p-4 rounded-xl">
                <div className="text-amber-400 font-bold mb-2 flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full bg-amber-500/20 text-amber-400 flex items-center justify-center text-xs">2</span>
                  Подання до МОУ / Brave1
                </div>
                <p className="text-xs text-slate-400">
                  Пакет подається до ГУЗР ОВТ МОУ або через портал mil-tech.mod.gov.ua. Спрощена процедура триває до 10–14 діб замість кількох місяців у мирний час.
                </p>
              </div>

              <div className="bg-slate-950 border border-slate-800 p-4 rounded-xl">
                <div className="text-amber-400 font-bold mb-2 flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full bg-amber-500/20 text-amber-400 flex items-center justify-center text-xs">3</span>
                  Наказ МОУ та Код НАТО
                </div>
                <p className="text-xs text-slate-400">
                  Видається Наказ Міністра оборони про допуск до експлуатації. Національне бюро кодифікації присвоює код NSN для офіційних державних закупівель.
                </p>
              </div>
            </div>

            <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-xl text-amber-200 text-xs">
              <span className="font-bold">Юридична довідка:</span> Згідно з Постановою КМУ № 345 виробник самостійно затверджує Технічні умови (ТУ У) та проводить визначальні відомчі випробування на підтвердження ТТХ. Затвердження ТУ державними органами у воєнний період скасовано.
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MilitaryCodifierAgentView;
